/**
 * Member Service
 * 
 * Handles all member/user-related API calls including:
 * - Member CRUD operations
 * - Member profiles and settings
 * - Invitations and relationships
 * - Channel management
 * - Organization members
 */

import api from './api';

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface Member {
  _id?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  [key: string]: any;
}

export interface Invitation {
  _id?: string;
  inviter?: string;
  invitee?: string;
  [key: string]: any;
}

// ============================================================================
// Member CRUD Operations
// ============================================================================

/**
 * API: GET /api/v2.0/member
 * Auth: yes
 * Gets all members
 */
export async function getMembers(params?: any): Promise<any> {
  return api.get('/member', { params });
}

/**
 * API: GET /api/v2.0/memberNew
 * Auth: yes
 * Gets members (new version)
 */
export async function getMembersNew(params?: any): Promise<any> {
  return api.get('/memberNew', { params });
}

/**
 * API: GET /api/v2.0/membershort
 * Auth: yes
 * Gets members with minimal information
 */
export async function getMembersShort(params?: any): Promise<any> {
  return api.get('/membershort', { params });
}

/**
 * API: GET /api/v2.0/membershortNew
 * Auth: yes
 * Gets members with minimal information (new version)
 */
export async function getMembersShortNew(params?: any): Promise<any> {
  return api.get('/membershortNew', { params });
}

/**
 * API: GET /api/v2.0/memberAdminApp
 * Auth: yes
 * Gets member information
 */
export async function getMemberAdminApp(params?: any): Promise<any> {
  return api.get('/memberAdminApp', { params });
}

/**
 * API: POST /api/v2.0/member
 * Auth: yes
 * Creates a new member
 */
export async function createMember(data: Member): Promise<any> {
  return api.post('/member', data);
}

/**
 * API: PUT /api/v2.0/member/:id
 * Auth: yes
 * Updates a member
 */
export async function updateMember(id: string, data: Member): Promise<any> {
  return api.put(`/member/${id}`, data);
}

/**
 * API: POST /api/v2.0/update/member/:id
 * Auth: yes
 * Updates a member (alternative endpoint)
 */
export async function updateMemberAlt(id: string, data: Member): Promise<any> {
  return api.post(`/update/member/${id}`, data);
}

/**
 * API: POST /api/v2.0/updateMember/:id
 * Auth: yes
 * Updates a member (app-specific endpoint)
 */
export async function updateMemberApp(id: string, data: Member): Promise<any> {
  return api.post(`/updateMember/${id}`, data);
}

/**
 * API: GET /api/v2.0/allMemberNames
 * Auth: yes
 * Gets all member names
 */
export async function getAllMemberNames(): Promise<any> {
  return api.get('/allMemberNames');
}

// ============================================================================
// Member Channel Management
// ============================================================================

/**
 * API: GET /api/v2.0/member/getCurrentChannel/:id
 * Auth: yes
 * Gets the current channel for a member
 */
export async function getCurrentChannel(id: string): Promise<any> {
  return api.get(`/member/getCurrentChannel/${id}`);
}

/**
 * API: GET /api/v2.0/member/getCurrentChannelApp/:id
 * Auth: yes
 * Gets the current channel for a member (app version)
 */
export async function getCurrentChannelApp(id: string): Promise<any> {
  return api.get(`/member/getCurrentChannelApp/${id}`);
}

/**
 * API: POST /api/v2.0/member/setCurrentChannel/:id
 * Auth: yes
 * Sets the current channel for a member
 */
export async function setCurrentChannel(id: string, data: { channel: string }): Promise<any> {
  return api.post(`/member/setCurrentChannel/${id}`, data);
}

/**
 * API: POST /api/v2.0/member/setCurrentChannelApp/:id
 * Auth: yes
 * Sets the current channel for a member (app version)
 */
export async function setCurrentChannelApp(id: string, data: { channel: string }): Promise<any> {
  return api.post(`/member/setCurrentChannelApp/${id}`, data);
}

/**
 * API: POST /api/v2.0/updateLastTwilioNumber
 * Auth: yes
 * Updates the last Twilio number used by a member
 */
export async function updateLastTwilioNumber(data: { memberId: string; number: string }): Promise<any> {
  return api.post('/updateLastTwilioNumber', data);
}

// ============================================================================
// Member Status Management
// ============================================================================

/**
 * API: POST /api/v2.0/setMemberActive/:id
 * Auth: yes
 * Sets a member as active
 */
export async function setMemberActive(id: string, data?: { active: boolean }): Promise<any> {
  return api.post(`/setMemberActive/${id}`, data);
}

// ============================================================================
// Invitations
// ============================================================================

/**
 * API: GET /api/v2.0/invitation
 * Auth: yes
 * Gets all invitations
 */
export async function getInvitations(params?: any): Promise<any> {
  return api.get('/invitation', { params });
}

/**
 * API: GET /api/v2.0/invitationNew
 * Auth: yes
 * Gets invitations (new version)
 */
export async function getInvitationsNew(params?: any): Promise<any> {
  return api.get('/invitationNew', { params });
}

/**
 * API: POST /api/v2.0/invitation
 * Auth: yes
 * Creates a new invitation
 */
export async function createInvitation(data: Invitation): Promise<any> {
  return api.post('/invitation', data);
}

/**
 * API: PUT /api/v2.0/invitation/:id
 * Auth: yes
 * Updates an invitation
 */
export async function updateInvitation(id: string, data: Invitation): Promise<any> {
  return api.put(`/invitation/${id}`, data);
}

/**
 * API: POST /api/v2.0/update/invitation/:id
 * Auth: yes
 * Updates an invitation (alternative endpoint)
 */
export async function updateInvitationAlt(id: string, data: Invitation): Promise<any> {
  try {
    return await api.post(`/update/invitation/${id}`, data);
  } catch (error: any) {
    const status = error?.response?.status;
    // Some controller environments reject /update/invitation and only accept PUT /invitation/:id.
    if (status === 404 || status === 405 || status === 500) {
      return api.put(`/invitation/${id}`, data);
    }
    throw error;
  }
}

/**
 * API: POST /api/v2.0/invitationMultiArgs
 * Auth: no
 * Gets invitations with multiple arguments
 */
export async function getInvitationMultiArgs(data: any): Promise<any> {
  return api.post('/invitationMultiArgs', data);
}

/**
 * API: POST /api/v2.0/sendMultipleInvitations
 * Auth: no
 * Sends multiple invitations at once
 */
export async function sendMultipleInvitations(data: { invitations: Invitation[] }): Promise<any> {
  return api.post('/sendMultipleInvitations', data);
}

/**
 * API: POST /api/v2.0/sendMultipleInvitationsNew
 * Auth: yes
 * Sends multiple invitations at once (new version)
 */
export async function sendMultipleInvitationsNew(data: { invitations: Invitation[] }): Promise<any> {
  return api.post('/sendMultipleInvitationsNew', data);
}

/**
 * API: POST /api/v2.0/validateMultipleInvitations
 * Auth: no
 * Validates multiple invitations
 */
export async function validateMultipleInvitations(data: { invitations: Invitation[] }): Promise<any> {
  return api.post('/validateMultipleInvitations', data);
}

/**
 * API: POST /api/v2.0/validateMultipleInvitationsNew
 * Auth: yes
 * Validates multiple invitations (new version)
 */
export async function validateMultipleInvitationsNew(data: { invitations: Invitation[] }): Promise<any> {
  return api.post('/validateMultipleInvitationsNew', data);
}

/**
 * API: POST /api/v2.0/revokeInvitation
 * Auth: no
 * Revokes an invitation (member invitation)
 */
export async function revokeInvitation(data: { id: string }): Promise<any> {
  return api.post('/revokeInvitation', {
    id: data.id,
    // TODO: Backend issue - remove hardcoded secret once backend fixed
    secret: "QHh3pjdqew29UkwGX3zV.N!CFiB",
  });
}

// ============================================================================
// NM (Network Member) Invitations
// ============================================================================

/**
 * API: GET /api/v1.0/getNMInvitationsNew
 * Auth: yes
 * Gets network member invitations
 */
export async function getNMInvitationsNew(params?: any): Promise<any> {
  return api.get('/getNMInvitationsNew', { 
    params,
    baseURL: '/api/v1.0' // Override baseURL for v1.0 endpoint
  });
}

/**
 * API: POST /api/v2.0/createNMInvitation
 * Auth: yes
 * Creates a network member invitation
 */
export async function createNMInvitation(data: any): Promise<any> {
  return api.post('/createNMInvitation', data);
}

/**
 * API: GET /api/v2.0/getLatestNMInvitation
 * Auth: yes
 * Gets the latest network member invitation
 */
export async function getLatestNMInvitation(params?: any): Promise<any> {
  return api.get('/getLatestNMInvitation', { params });
}

/**
 * API: POST /api/v2.0/setLatestNMInvitation/:invitee
 * Auth: yes
 * Sets the latest network member invitation for an invitee
 */
export async function setLatestNMInvitation(invitee: string, data: any): Promise<any> {
  return api.post(`/setLatestNMInvitation/${invitee}`, data);
}

/**
 * API: POST /api/v2.0/exportNMInvitations/:invitee
 * Auth: yes
 * Exports network member invitations for an invitee
 */
export async function exportNMInvitations(invitee: string, data?: any): Promise<any> {
  return api.post(`/exportNMInvitations/${invitee}`, data);
}

/**
 * API: POST /api/v2.0/revokeInvitationOfferNM
 * Auth: no
 * Revokes a network member invitation offer (non-member offer)
 */
export async function revokeInvitationOfferNM(data: { id: string }): Promise<any> {
  return api.post('/revokeInvitationOfferNM', {
    id: data.id,
        // TODO: Backend issue - remove hardcoded secret once backend fixed

    secret: "QHh3pjdqew29UkwGX3zV.N!CFiB",
  });
}

// ============================================================================
// Offers
// ============================================================================

/**
 * API: POST /api/v2.0/sendMultipleOffers
 * Auth: no
 * Sends multiple offers at once
 */
export async function sendMultipleOffers(data: { offers: any[] }): Promise<any> {
  return api.post('/sendMultipleOffers', data);
}

/**
 * API: POST /api/v2.0/sendMultipleOffersNew
 * Auth: yes
 * Sends multiple offers at once (new version)
 */
export async function sendMultipleOffersNew(data: { offers: any[] }): Promise<any> {
  return api.post('/sendMultipleOffersNew', data);
}

/**
 * API: POST /api/v2.0/validateMultipleOffers
 * Auth: no
 * Validates multiple offers
 */
export async function validateMultipleOffers(data: { offers: any[] }): Promise<any> {
  return api.post('/validateMultipleOffers', data);
}

/**
 * API: POST /api/v2.0/validateMultipleOffersNew
 * Auth: yes
 * Validates multiple offers (new version)
 */
export async function validateMultipleOffersNew(data: { offers: any[] }): Promise<any> {
  return api.post('/validateMultipleOffersNew', data);
}

/**
 * API: POST /api/v2.0/revokeOffer
 * Auth: no
 * Revokes an offer (member offer)
 */
export async function revokeOffer(data: { id: string }): Promise<any> {
  return api.post('/revokeOffer', {
    id: data.id,
        // TODO: Backend issue - remove hardcoded secret once backend fixed
    secret: "QHh3pjdqew29UkwGX3zV.N!CFiB",
  });
}

// ============================================================================
// Relationships
// ============================================================================

/**
 * API: GET /api/v2.0/getRelationships
 * Auth: yes
 * Gets member relationships
 */
export async function getRelationships(params?: any): Promise<any> {
  return api.get('/getRelationships', { params });
}

// ============================================================================
// Organization Members
// ============================================================================

/**
 * API: POST /api/v2.0/getMembersByOrganization
 * Auth: no
 * Gets all members in an organization
 */
export async function getMembersByOrganization(data: { organizationId: string }): Promise<any> {
  return api.post('/getMembersByOrganization', data);
}

/**
 * API: POST /api/v2.0/getMembersByOrganizationShort
 * Auth: no
 * Gets members in an organization with minimal information
 */
export async function getMembersByOrganizationShort(data: { organizationId: string }): Promise<any> {
  return api.post('/getMembersByOrganizationShort', data);
}

// ============================================================================
// Invitations and Offers (Multiple)
// ============================================================================

/**
 * API: POST /api/v2.0/validateMultipleOffersNew
 * Auth: yes
 * Validates multiple offers before submission
 */
export async function validateOffersMultiple(data: { badge: string; contacts: string[] }): Promise<any> {
  return api.post('/validateMultipleOffersNew', data);
}

/**
 * API: POST /api/v2.0/sendMultipleOffersNew
 * Auth: yes
 * Creates multiple offers (for "Make a Promise" - OFFER)
 */
export async function newOffersMultiple(data: {
  committer: string;
  badge: string;
  promise: string;
  dueDate: string;
  contacts: string[];
  source: string;
  questionsToAnswer?: string;
}): Promise<any> {
  return api.post('/sendMultipleOffersNew', data);
}

/**
 * API: POST /api/v2.0/validateMultipleInvitationsNew
 * Auth: yes
 * Validates multiple invitations before submission
 */
export async function validateInvitationsMultiple(data: { badge: string; contacts: string[] }): Promise<any> {
  return api.post('/validateMultipleInvitationsNew', data);
}

/**
 * API: POST /api/v2.0/sendMultipleInvitationsNew
 * Auth: yes
 * Creates multiple invitations (for "Request a Commitment" - INVITATION)
 */
export async function newInvitationsMultiple(data: {
  issuer: string;
  badge: string;
  promise: string;
  contacts: string[];
  source: string;
}): Promise<any> {
  return api.post('/sendMultipleInvitationsNew', data);
}
